import { ForemanJobDetail } from '@ups/xplat/api/dto';

import { jobsShareForemanIdentity } from './foreman-job-key.utils';
import { reconcileDefaultWorkOrdersFromRemoteJob } from './foreman-job-remote-default-wo-reconcile.utils';
import { reconcileEquipmentDatesFromRemoteJob } from './foreman-job-remote-dispatch-dates-reconcile.utils';
import { reconcileTimesheetBlockFromRemoteJob } from './foreman-job-remote-timesheet-block-reconcile.utils';

/**
 * Overlay the office/VP-owned fields of a remote job onto a cached or open one: default work orders, equipment
 * dispatch dates and each phase's timesheet block. Every foreman-owned field is left alone. `keepLocalPhases` (the open
 * job) keeps a default work order the foreman added on the device after the server's list (review 109265).
 */
export function reconcileOfficeOwnedFieldsFromRemoteJob(
  job: ForemanJobDetail,
  remoteJob: ForemanJobDetail,
  keepLocalPhases = false
): void {
  reconcileDefaultWorkOrdersFromRemoteJob(job, remoteJob, keepLocalPhases);
  reconcileEquipmentDatesFromRemoteJob(job, remoteJob);
  reconcileTimesheetBlockFromRemoteJob(job, remoteJob);
}

/** The open-job snapshots this module set, so the job-change effect can tell a field refresh from a job switch. */
const officeRefreshes = new WeakSet<ForemanJobDetail>();

/**
 * Bug 32137: a job-list refresh reconciles the office-owned fields into the CACHE, but the job the foreman has open
 * (`selectedJob`) kept its stale copies, and the next persist of `selectedJob` wrote them back over the cache (a VP
 * timesheet block, a moved dispatch window or new default work orders reverted). This patches the open job from the
 * remote list at the layer where remote is authoritative: the nested work order, equipment and crew rows are updated
 * IN PLACE, so a page holding a row keeps editing the live object, and only office-owned fields change, so a
 * foreman's unsynced hours are untouched. Returns a shallow copy to `set` (so computed reads refresh) when a field
 * changed, else null. A job opened from `getForemanJobDetail` is never overlaid from the cache: only this list
 * response, read now from the server, is applied.
 */
export function reconcileOpenJobFromRemote(
  openJob: ForemanJobDetail | null | undefined,
  remoteJobs: readonly ForemanJobDetail[]
): ForemanJobDetail | null {
  if (!openJob) {
    return null;
  }
  const remoteJob = remoteJobs.find((r) =>
    jobsShareForemanIdentity(r, openJob)
  );
  if (!remoteJob) {
    return null;
  }
  const before = JSON.stringify(openJob);
  reconcileOfficeOwnedFieldsFromRemoteJob(openJob, remoteJob, true);
  if (JSON.stringify(openJob) === before) {
    return null;
  }
  const refreshed = { ...openJob };
  officeRefreshes.add(refreshed);
  return refreshed;
}

/**
 * True once for a snapshot `reconcileOpenJobFromRemote` returned, when the job the effect last saw is the same job:
 * office fields refreshed, not a job switch, so the job-change effect keeps the Job Owner envelope and the loaded
 * purchase orders. The identity check keeps a stale mark from hiding a real switch (review 109265).
 */
export function isOfficeRefreshOfOpenJob(
  job: ForemanJobDetail | null | undefined,
  previousJob: ForemanJobDetail | null | undefined
): boolean {
  return (
    !!job &&
    officeRefreshes.delete(job) &&
    !!previousJob &&
    jobsShareForemanIdentity(job, previousJob)
  );
}
